import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AttachmentUpload, type AttachmentUploadItem } from "./motion/attachment-upload";

type Props = {
  value: File[];
  previews: { file: File; url: string }[];
  max: number;
  multiple: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  onRejected: () => void;
};

/** Cropped files remain caller-owned; this view never uploads or replaces their bytes. */
export function PhotoAttachmentPicker({ value, previews, max, multiple, disabled, onFiles, onRemove, onMove, onRejected }: Props) {
  const identities = useRef(new WeakMap<File, string>());
  const latest = useRef({ value, onRemove });
  latest.current = { value, onRemove };
  const items: AttachmentUploadItem[] = value.map(file => {
    let id = identities.current.get(file);
    if (!id) { id = crypto.randomUUID(); identities.current.set(file, id); }
    return { id, name: file.name, kind: "image", file, size: file.size,
      previewUrl: previews.find(preview => preview.file === file)?.url, status: "idle" };
  });
  return <AttachmentUpload value={items} accept="image/*" inputLabel={multiple ? "Choose photos" : "Choose photo"}
    multiple={multiple} maxFiles={max} maxFileSize={Infinity} disabled={disabled} title="Add photos"
    description="First photo is the cover · Cropped to 4:3" attachmentsLabel="Selected photos"
    classNames={{ list: "max-h-[19.5rem] overflow-y-auto overscroll-contain [scrollbar-gutter:stable]" }}
    onFilesAdded={(_items, files) => onFiles(files)} onFilesRejected={onRejected}
    onRemove={item => {
      // Removal animates before calling back; resolve against the latest order.
      const index = latest.current.value.findIndex(file => file === item.file);
      if (index >= 0) latest.current.onRemove(index);
    }}
    itemActions={item => {
      const index = value.findIndex(file => file === item.file);
      return <div className="flex shrink-0 items-center gap-1">
        {index === 0 && <span className="rounded bg-brand-gold/20 px-1.5 py-1 text-[10px] font-bold text-navy">Cover</span>}
        {value.length > 1 && <>
          <button type="button" className="btn-ghost h-11 w-9 p-0 disabled:opacity-30" disabled={index === 0 || disabled} aria-label={`Move photo ${index + 1} earlier`} onClick={() => onMove(index, -1)}><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" className="btn-ghost h-11 w-9 p-0 disabled:opacity-30" disabled={index === value.length - 1 || disabled} aria-label={`Move photo ${index + 1} later`} onClick={() => onMove(index, 1)}><ChevronRight className="h-4 w-4" /></button>
        </>}
      </div>;
    }} />;
}
